import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useContextChain } from "../state/ContextChain";

/**
 * Cat 24 Feature Flags — FE side. Fetches the workspace-resolved flag map once
 * (GET /api/workspace/flags?workspace_id=…) and exposes it via context; `useFlag`
 * resolves a key against the map with a caller-supplied default. Default-safe: the
 * map starts empty, so every flag resolves to its default until (and unless) a flag
 * row is configured — the system is behavior-neutral by default.
 */
export type FlagMap = Record<string, boolean>;

const FlagsContext = createContext<FlagMap>({});

export function resolveFlag(map: FlagMap, key: string, dflt: boolean): boolean {
  return Object.prototype.hasOwnProperty.call(map, key) ? map[key]! : dflt;
}

/** Resolve a single flag against the provided context map. */
export function useFlag(key: string, dflt: boolean): boolean {
  return resolveFlag(useContext(FlagsContext), key, dflt);
}

export interface FlagsProviderProps {
  children: ReactNode;
  apiBase?: string;
  fetchImpl?: typeof fetch;
  /** Test injection: when provided, skips the fetch and uses this map directly. */
  value?: FlagMap;
}

export function FlagsProvider({ children, apiBase = "/api/workspace", fetchImpl, value }: FlagsProviderProps): React.ReactElement {
  const { workspaceId } = useContextChain();
  const [map, setMap] = useState<FlagMap>(value ?? {});

  useEffect(() => {
    if (value) return; // injected — do not fetch
    const f = fetchImpl ?? fetch;
    const base = apiBase.replace(/\/$/, "");
    let cancelled = false;
    (async () => {
      try {
        const qs = workspaceId ? `?workspace_id=${encodeURIComponent(workspaceId)}` : "";
        const res = await f(`${base}/flags${qs}`, { method: "GET" });
        if (!res.ok) return; // fail-open to defaults — a flags outage must not break the shell
        const data = (await res.json()) as FlagMap;
        if (!cancelled) setMap(data);
      } catch {
        // network/parse error → keep defaults (fail-open)
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workspaceId, apiBase, fetchImpl, value]);

  return <FlagsContext.Provider value={map}>{children}</FlagsContext.Provider>;
}

// Exported for tests that need to provide a static map without the fetch path.
export { FlagsContext };
